import * as RPopover from '@radix-ui/react-popover'
import { type ReactNode } from 'react'

import { cn } from '../cn'

// Popover — contextual content anchored to a trigger (explanation, mini-form)
// (design/components/overlays.html §5.2). Built on Radix Popover (focus
// management, Esc, outside-click). Unlike a Tooltip it may hold controls.

export interface PopoverProps {
  trigger: ReactNode
  children: ReactNode
  placement?: 'top' | 'bottom' | 'left' | 'right'
  /** Accessible label when the content has no heading. */
  label?: string
  className?: string
  /** Controlled open state (Radix-style). Omit both for uncontrolled — the
   *  default every existing caller keeps. A caller that needs to know when the
   *  panel opens/closes (e.g. to snapshot state for its lifetime) passes both. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

/** Contextual panel anchored to its trigger (Radix Popover: focus management,
 *  Esc, outside-click). Unlike Tooltip it may hold controls; `trigger` renders
 *  asChild, so pass a real button. */
export function Popover({
  trigger,
  children,
  placement = 'bottom',
  label,
  className,
  open,
  onOpenChange,
}: PopoverProps) {
  return (
    <RPopover.Root
      {...(open !== undefined ? { open } : {})}
      {...(onOpenChange ? { onOpenChange } : {})}
    >
      <RPopover.Trigger asChild>{trigger}</RPopover.Trigger>
      <RPopover.Portal>
        <RPopover.Content
          side={placement}
          sideOffset={6}
          aria-label={label}
          className={cn(
            'z-50 w-[236px] rounded-lg border border-line bg-surface p-3.5 text-[11.5px] leading-normal text-text-2 shadow-card focus:outline-none',
            className,
          )}
        >
          {children}
          <RPopover.Arrow className="fill-surface" width={11} height={6} />
        </RPopover.Content>
      </RPopover.Portal>
    </RPopover.Root>
  )
}
